/**
 * Explore date-window math (POS2-880, reworked POS2-1000; site-tz anchoring POS2-1036).
 * The FE owns the window math and passes explicit [from, to) instants to the pivot RPC
 * (POS2-881). Both the RPC (`date_trunc(... AT TIME ZONE p_timezone)`) and the chart's
 * bucket keys reinterpret those instants in the SITE timezone, so the bounds must be built
 * at site-tz midnight — not the viewer's — or the first/last bucket is off by a day. The
 * anchoring helpers themselves live in `@/lib/zoned-day` (shared with the Dashboard's
 * windows since POS2-1039).
 *
 * Two window sources, both producing a concrete [from, to) span:
 *  - The granularity's default window: daily → last 7 days, weekly → last 91 days
 *    (13 weeks), monthly → last 12 full calendar months.
 *  - A custom range set in the Filters popover, recovered from the stored instants and
 *    re-anchored to the site tz in `useExploreState`.
 */
import { format } from "date-fns";

import { shiftDay, zonedDay, zonedDayStart } from "@/lib/zoned-day";

export type Granularity = "daily" | "weekly" | "monthly";

export type ExploreWindow = { from: Date; to: Date };

/** Default span length in days for the day-based granularities (POS2-1000). */
const DEFAULT_DAYS: Record<"daily" | "weekly", number> = { daily: 7, weekly: 91 };

/**
 * The default [from, to) window for a granularity, anchored to `timeZone` midnight so it lines
 * up with the RPC's site-local buckets.
 *  - daily/weekly: fixed-length day blocks ending at the start of today.
 *  - monthly: the last 12 full calendar months (upper bound = start of this month, so the
 *    buckets are whole months with no leading partial — POS2-881 AC).
 */
export function defaultWindow(gran: Granularity, now: Date, timeZone: string): ExploreWindow {
  const today = zonedDay(now, timeZone);
  if (gran === "monthly") {
    const [y, m] = today.split("-").map(Number);
    const firstOfMonth = (year: number) => `${year}-${String(m).padStart(2, "0")}-01`;
    return {
      from: zonedDayStart(firstOfMonth(y - 1), timeZone),
      to: zonedDayStart(firstOfMonth(y), timeZone),
    };
  }
  return {
    from: zonedDayStart(shiftDay(today, -DEFAULT_DAYS[gran]), timeZone),
    to: zonedDayStart(today, timeZone),
  };
}

/**
 * The `from` bound of a default-length window whose last included day is `lastDay` — the same
 * span as `defaultWindow`, slid to end where the caller says.
 *
 * This is what an open START resolves to (POS2-1051). "Up to 10 Jul" has to mean the usual span
 * ending on the 10th; taking `defaultWindow`'s own `from` meant a fixed date that could sit
 * *after* the given end, which inverted the window and reported zero records.
 */
export function defaultSpanStart(gran: Granularity, lastDay: string, timeZone: string): Date {
  if (gran === "monthly") {
    const [y, m] = lastDay.split("-").map(Number);
    // 12 months inclusive of lastDay's own month, so 11 whole months back from it.
    const months = y * 12 + (m - 1) - 11;
    const first = `${Math.floor(months / 12)}-${String((months % 12) + 1).padStart(2, "0")}-01`;
    return zonedDayStart(first, timeZone);
  }
  // `defaultWindow` spans DEFAULT_DAYS days ending the day before `today`, so an inclusive
  // last day is one short of the full step back.
  return zonedDayStart(shiftDay(lastDay, -(DEFAULT_DAYS[gran] - 1)), timeZone);
}

/** Concrete span label from inclusive civil-day strings ("8 Jun – 5 Jul 2026", "Jul 2025 –
 * Jun 2026"). Civil-day inputs keep the label right for any viewer, whatever their browser tz. */
export function labelSpan(gran: Granularity, fromDay: string, lastDay: string): string {
  const from = parseDay(fromDay);
  const last = parseDay(lastDay);
  if (gran === "monthly") {
    return `${format(from, "MMM yyyy")} – ${format(last, "MMM yyyy")}`;
  }
  const sameYear = fromDay.slice(0, 4) === lastDay.slice(0, 4);
  return `${format(from, sameYear ? "d MMM" : "d MMM yyyy")} – ${format(last, "d MMM yyyy")}`;
}

/** "YYYY-MM-DD" → LOCAL midnight Date, only for label formatting (the day parts are what
 * matter, so the browser tz is irrelevant here). */
function parseDay(day: string): Date {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y, m - 1, d);
}
